import { type Common } from '../common.js';
import { ErrorType } from '../models/errorHelper.js';
import {
  type CreateValues,
  type FindAllOptions,
  type Organization,
} from '../models/organization/index.js';
import { type PaginatedQueryOptions, type PaginationResponse } from '../models/pagination/index.js';
import { type SamlSetting } from '../models/samlSetting.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/organizations';
  }

  async create(toAdd: CreateValues): Promise<Organization> {
    const url = this.common.constructPath(this.baseEndpoint);
    const { organization } = await this.common.postJSON(url, toAdd);
    return organization;
  }

  async findAll(): Promise<Organization[]> {
    const url = this.common.constructPath(this.baseEndpoint);
    const { organizations } = await this.common.getJSON(url);
    return organizations;
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<Organization>> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<Organization> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { organization } = await this.common.getJSON(url);
    return organization;
  }

  async findMaybeOneById(id: string): Promise<Organization | undefined> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/maybe`);
    const { organization } = await this.common.getJSON(url);
    return organization ?? undefined;
  }

  async setSsoEnabled(id: string, ssoEnabled: boolean): Promise<Organization> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/sso`);
    const { organization } = await this.common.patchJSON(url, {
      ssoEnabled,
    });
    return organization;
  }

  async findSamlSetting(organizationId: string): Promise<SamlSetting | null> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${organizationId}/saml-setting`);
    try {
      const { samlSetting } = await this.common.getJSON(url);
      return samlSetting;
    } catch (error) {
      if (error instanceof Error && error.message.includes(`"type":"${ErrorType.NOT_FOUND}"`)) {
        /**
         * FIXME: Catching the not-found case by substring-matching error.message is brittle because APIError formats its message via JSON.stringify and the string contents/order can change.
         * Prefer checking the structured error information (e.g., status === 404 when present, or parsing the JSON payload and checking type === ErrorType.NOT_FOUND).
         */
        return null;
      }
      throw error;
    }
  }
}
